import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext.jsx'
import {
  DocumentTextIcon,
  ArrowRightStartOnRectangleIcon,
  UserCircleIcon,
  CalendarDaysIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline'

function formatDate(dateString) {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

export default function Dashboard() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Top nav */}
      <nav className="border-b border-gray-800 bg-gray-900/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <span className="font-bold text-lg tracking-tight text-white">Karimel</span>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
          >
            <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
            Logout
          </button>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        {/* Welcome header */}
        <div>
          <h1 className="text-2xl font-bold text-white">
            Welcome back, {user?.name || 'there'} 👋
          </h1>
          <p className="mt-1 text-sm text-gray-400">Here's your dashboard overview.</p>
        </div>

        {/* Profile card */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-12 w-12 rounded-full bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center">
              <UserCircleIcon className="h-7 w-7 text-indigo-400" />
            </div>
            <div>
              <h2 className="font-semibold text-white">{user?.name}</h2>
              <p className="text-sm text-gray-400">Account details</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <EnvelopeIcon className="h-4 w-4 text-gray-500 shrink-0" />
              <span className="text-gray-400">Email</span>
              <span className="ml-auto text-gray-200">{user?.email}</span>
            </div>
            <div className="h-px bg-gray-800" />
            <div className="flex items-center gap-3 text-sm">
              <CalendarDaysIcon className="h-4 w-4 text-gray-500 shrink-0" />
              <span className="text-gray-400">Member since</span>
              <span className="ml-auto text-gray-200">{formatDate(user?.created_at)}</span>
            </div>
          </div>
        </div>

        {/* Quick links */}
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Navigation</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link
              to="/notes"
              className="group flex items-center gap-4 bg-gray-900 border border-gray-800 hover:border-indigo-500/50 hover:bg-gray-800/60 rounded-xl p-4 transition-all"
            >
              <div className="h-10 w-10 rounded-lg bg-indigo-600/15 border border-indigo-500/25 flex items-center justify-center group-hover:bg-indigo-600/25 transition-colors">
                <DocumentTextIcon className="h-5 w-5 text-indigo-400" />
              </div>
              <div>
                <p className="font-medium text-white">Notes</p>
                <p className="text-xs text-gray-400">View and manage your notes</p>
              </div>
            </Link>
          </div>
        </div>
      </main>
    </div>
  )
}
